import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';

import { courseLessonTypes } from './courseLessonTypes';
import { Interface } from '../interfaces/Interface';
import { Tutorial } from '../tutorial/Tutorial';
import {
   clearActiveInterface,
   clearTutorial,
   setTutorial,
} from '../app/actions';
import { ExerciseWrapper } from '../exercises/Exercise';
import { ShowSiteLoading } from '../SiteLoadingIndicator';
import { courseDocumentQueryOptions } from './queryGroups';
import { useSetLessonComplete } from './useSetLessonComplete';
import { ContentScreen } from '../styles/ContentScreen';
import { Button } from '../components/Button';

function CourseLesson() {
   const { courseId, lessonId } = useParams();

   const { data: courseDocument } = useQuery(
      courseDocumentQueryOptions(courseId)
   );

   if (courseDocument == null) {
      return <ShowSiteLoading />;
   }

   return <LessonDisplay courseDocument={courseDocument} lessonId={lessonId} />;
}

function LessonDisplay({ courseDocument, lessonId }) {
   const navigate = useNavigate();
   const setLessonComplete = useSetLessonComplete(courseDocument.id);

   const { lessonInfo, nextLessonInfo } = useMemo(() => {
      let lessonInfo;
      let nextLessonInfo;
      for (let i = 0; i < courseDocument.sections.length; i++) {
         for (let j = 0; j < courseDocument.sections[i].lessons.length; j++) {
            const currentId = courseDocument.sections[i].lessons[j].id;
            // ToDo: maybe account for whether the lesson is complete or not
            // either navigate to next incomplete section... or navigate back to overview
            if (lessonInfo != null) {
               nextLessonInfo = courseDocument.sections[i].lessons[j];
               return { lessonInfo, nextLessonInfo };
            }
            if (currentId === lessonId) {
               lessonInfo = courseDocument.sections[i].lessons[j];
            }
         }
      }
      return { lessonInfo, nextLessonInfo };
   }, [lessonId, courseDocument]);

   const completeLesson = useCallback(() => {
      setLessonComplete({
         lessonId: lessonInfo.id,
         newCompletedValue: true,
         trackDate: true,
      });
      // send api request to update course progress - either just to complete lesson or to sync
      // navigate to next lesson

      navigate(
         nextLessonInfo != null
            ? `/app/course/${courseDocument.id}/${nextLessonInfo.id}`
            : `/app/course/${courseDocument.id}`
      );
   }, [courseDocument, lessonInfo, nextLessonInfo, setLessonComplete]);

   if (lessonInfo == null) {
      return <Navigate to={`/app/course/${courseDocument.id}`} />;
   }

   if (lessonInfo.type === courseLessonTypes.tutorial) {
      return (
         <TutorialLesson
            onComplete={completeLesson}
            courseId={courseDocument.id}
            tutorialId={lessonInfo.content.tutorialId}
         />
      );
   }
   if (lessonInfo.type === courseLessonTypes.article) {
      return (
         <ArticleLesson
            title={lessonInfo.title}
            videoId={lessonInfo.content.videoId}
            onComplete={completeLesson}
         />
      );
   }
   if (lessonInfo.type === courseLessonTypes.checklist) {
      return <ChecklistLesson />;
   }
}

const mapStateToPropsTutorialLesson = ({ tutorial }) => ({
   ready: tutorial?.ready,
});

const mapDispatchToPropsTutorialLesson = {
   setTutorial,
   clearTutorial,
   clearActiveInterface,
};

const TutorialLesson = connect(
   mapStateToPropsTutorialLesson,
   mapDispatchToPropsTutorialLesson
)(
   ({
      ready,
      setTutorial,
      clearTutorial,
      clearActiveInterface,
      onComplete,
      courseId,
      tutorialId,
   }) => {
      useEffect(() => {
         setTutorial({
            courseId,
            tutorialId,
            secure: true,
         });

         return () => {
            clearTutorial();
            clearActiveInterface();
         };
      }, [setTutorial, tutorialId]);

      if (!ready) {
         return <ShowSiteLoading />;
      }

      return (
         <>
            <ExerciseWrapper className="tutorial-active" data-interface-wrapper>
               <Interface />
            </ExerciseWrapper>
            <Tutorial onComplete={onComplete} />
         </>
      );
   }
);

function ArticleLesson({ title, videoId, onComplete }) {
   return (
      <ContentScreen>
         {videoId && (
            <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
               <iframe
                  src={`https://player.vimeo.com/video/${videoId}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  style={{
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     width: '100%',
                     height: '100%',
                  }}
                  title={title}
               ></iframe>
            </div>
         )}
         <p style={{ textAlign: 'center', marginTop: '2em' }}>
            <Button className="button-cta" onClick={onComplete}>
               Next Lesson
            </Button>
         </p>
      </ContentScreen>
   );
}

function ChecklistLesson() {
   return 'checklist';
}

export { CourseLesson };
